import * as React from "react";
import {
  SingleNavMenuLink,
  toLinkButton,
  toLinkCategory,
  toLinkCategoryEnd,
  toLinkDivider,
  toLinkPage,
  toLinkSpace,
} from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Identity } from "../../schema/types/identity/entity";
import { IdentityDetailHeader } from "./header";
import { Summary } from "./summary";
import { IdentityCreateCloudaccessAWS } from "./create/cloudaccess/aws";
import { IdentityCreateCloudaccessAzure } from "./create/cloudaccess/azure";
import { IdentityCreateCloudaccessNGS } from "./create/cloudaccess/ngs";
import { IdentityCreateCloudaccessGCP } from "./create/cloudaccess/gcp";
import { IdentityCreateNetworkResourceIP } from "./ipResource";
import { IdentityCreateNetworkResourceFQDN } from "./fqdnResource";
import { IdentityCreateNetworkResourceGCP } from "./gcpResource";
import { IdentityCreateNetworkResourceAWS } from "./awsResource";
import { CpRootError } from "../gvc/testNewForm/CpRootError";
import { CloudAccessOverview } from "./cloudAccessOverview";

interface Props {
  identity: Identity;
  refetch?: () => void;
}
export const IdentityDetailContent: React.FC<Props> = ({ identity, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/gvc/:gvc/identity/:identity/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(identity).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(identity.lastModified);
  }, [identity.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const navigate = useNavigate();

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  // Navigate back to cloud access overview after save
  const prevVersionRef = React.useRef(identity.version);
  React.useEffect(() => {
    if (identity.version !== prevVersionRef.current) {
      prevVersionRef.current = identity.version;
      const isOnCloudAccessEdit = ["-aws", "-azure", "-gcp", "-ngs"].some((suffix) =>
        pathname.includes(`/cloud-access${suffix}`),
      );
      if (isOnCloudAccessEdit) {
        navigate(`${basePath}/cloud-access`, { replace: true });
      }
    }
  }, [identity.version]);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Identity", "/summary", { names: [`description`], showDirty: true }),
      toLinkCategory("Cloud Access"),
      toLinkPage("Overview", "/cloud-access", { names: ["aws", "azure", "gcp", "ngs"], showDirty: true }),
      toLinkPage("AWS", "/cloud-access-aws", { names: ["aws"], showDirty: true }),
      toLinkPage("Azure", "/cloud-access-azure", { names: ["azure"], showDirty: true }),
      toLinkPage("GCP", "/cloud-access-gcp", { names: ["gcp"], showDirty: true }),
      toLinkPage("Nats", "/cloud-access-ngs", { names: ["ngs"], showDirty: true }),
      toLinkCategoryEnd(),
      toLinkCategory("Cloud Wormhole"),
      toLinkPage("FQDN Resources", "/cloud-wormhole-fqdn", { names: ["networkResources-fqdn"], showDirty: true }),
      toLinkPage("IP Resources", "/cloud-wormhole-ip", { names: ["networkResources-ip"], showDirty: true }),
      toLinkCategoryEnd(),
      toLinkCategory("Native Networking"),
      toLinkPage("AWS Private Link", "/native-networking-aws", {
        names: ["nativeNetworkResources-aws"],
        showDirty: true,
      }),
      toLinkPage("GCP Service Connect", "/native-networking-gcp", {
        names: ["nativeNetworkResources-gcp"],
        showDirty: true,
      }),
      toLinkCategoryEnd(),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <IdentityDetailHeader identity={identity} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary identity={identity} />} />
              <Route path={`cloud-access`} element={<CloudAccessOverview />} />
              <Route path={`cloud-access-aws/*`} element={<IdentityCreateCloudaccessAWS />} />
              <Route path={`cloud-access-azure/*`} element={<IdentityCreateCloudaccessAzure />} />
              <Route path={`cloud-access-gcp/*`} element={<IdentityCreateCloudaccessGCP />} />
              <Route path={`cloud-access-ngs/*`} element={<IdentityCreateCloudaccessNGS />} />
              <Route path={`cloud-wormhole-fqdn/*`} element={<IdentityCreateNetworkResourceFQDN />} />
              <Route path={`cloud-wormhole-ip/*`} element={<IdentityCreateNetworkResourceIP />} />
              <Route path={`native-networking-aws/*`} element={<IdentityCreateNetworkResourceAWS />} />
              <Route path={`native-networking-gcp/*`} element={<IdentityCreateNetworkResourceGCP />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"identity"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail
                    kind={"identity"}
                    itemName={identity.name}
                    resourceId={identity.id}
                    version={identity.version}
                    selfLink={linksOf(identity).self!}
                    refetch={refetch}
                  />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(identity).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
